import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { readerService, type Student } from '@/services/reader.service';
import { getAuthToken } from '@/services/api';

/**
 * Auth Store (Sprint 1, 2026-05-07)
 *
 * Gerencia o estado do Student logado no reader-auth (isolado).
 */
export const useAuthStore = defineStore('auth', () => {
  const student = ref<Student | null>(null);
  const hydrated = ref(false);
  const loading = ref(false);

  const isAuthenticated = computed(() => student.value !== null);

  /**
   * Tenta restaurar sessão a partir do token local.
   * Chamado pelo router guard antes da primeira navegação.
   */
  async function hydrate() {
    if (hydrated.value) return;
    if (!getAuthToken()) {
      student.value = null;
      hydrated.value = true;
      return;
    }
    try {
      student.value = await readerService.me();
    } catch {
      student.value = null;
    } finally {
      hydrated.value = true;
    }
  }

  async function login(email: string, password: string) {
    loading.value = true;
    try {
      const result = await readerService.login(email, password);
      student.value = result.student;
      hydrated.value = true;
      return result;
    } finally {
      loading.value = false;
    }
  }

  async function logout() {
    await readerService.logout();
    clearSession();
  }

  function clearSession() {
    student.value = null;
    hydrated.value = true;
  }

  /**
   * Define o student da sessão atual sem chamar a API.
   * Usado em fluxos onde o student já foi obtido por outro caminho
   * (ex.: magic link, onde `magicLinkService.exchange()` retorna o student
   * e já persiste o JWT em localStorage via setAuthToken).
   */
  function setSession(payload: { student: Student }) {
    student.value = payload.student;
    hydrated.value = true;
  }

  return {
    student,
    hydrated,
    loading,
    isAuthenticated,
    hydrate,
    login,
    logout,
    clearSession,
    setSession
  };
});
